import { Avatar, Box, Button, Divider, Flex, Grid, Icon, LinkBox, LinkOverlay, Menu, MenuButton, MenuItem, MenuList, Stack, Text } from '@chakra-ui/react'
import { useClearInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { Link } from 'react-router-dom'

import { useCommonSignalR } from '@/context/CommonSignalRProvider'
import { Citizen } from '@/icons'

import { PoleasEssoxImportButton } from '@/containers/PoleasEssoxImportButton'
import { useSwitchPartner } from '@/hooks/useSwitchPartner'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { faBadgePercent, faChevronRight, faHeadset, faMoneyBill, faNewspaper, faRightFromBracket, faShareNodes, faShieldKeyhole, faTypewriter, faUser, faUsers, type IconDefinition } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { UserRole } from 'BrokerTsLib/src/dtos'
import { useContext } from 'react'
import { useAuth } from '../../../context/AuthContext'
import { usePartner } from '../../../context/PartnerContext'
import { DropdownMenuItem, DropdownMenuItemContext } from '../MenuItem'

export const User = () => {
  const ctx = useAuth()
  const user = ctx?.user
  const { currentPartner } = usePartner()

  const signalR = useCommonSignalR()

  const clearInsuranceParamsFormPersistance = useClearInsuranceParamsFormPersistance()
  const logout = () => {
    clearInsuranceParamsFormPersistance()
    signalR.invoke('InvokeLogoutAll').then(() => {
      console.debug('User logged out.')
    })
      .catch((e) => {
        console.debug('Error while logging out:', e)
      })
  }

  const partner = currentPartner?.partner

  const showSettings = user?.roles?.some(role => isIn(role, UserRole.Extended, UserRole.Full, UserRole.OuSupervizor))
  const hasExtendedRights = user?.roles?.some(role => isIn(role, UserRole.Extended, UserRole.Full))

  const switchPartner = useSwitchPartner()

  return (
    <DropdownMenuItem
      label={
        <Flex gap={2}>
          <Citizen boxSize={4} />
          <Text
            sx={{
              ml: 1,
              fontSize: 'sm',
              fontWeight: 'semibold',
              whiteSpace: 'nowrap',
            }}
          >
            {user?.userFullName}
          </Text>
        </Flex>
      }
    >
      <UserAvatarMenuItem to='/profile'>
        <Avatar name={user?.userFullName} colorScheme="primary" size='sm' />
        <Stack gap={0}>
          <Text fontSize="sm" fontWeight='semibold' color='primary.900'>
            {user?.userFullName}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {user?.email}
          </Text>
        </Stack>
      </UserAvatarMenuItem>

      {partner !== 'Jap' && partner !== 'Essox' && user?.allowNews && <UserMenuItem label='Aktuality' to='/news' icon={faNewspaper} />}

      {user?.enablePoleasEssox && <PoleasEssoxImportButton />}

      {user?.isSupervisor && <>
        <UserMenuItem label='Přejít na HelpDesk' to="/api/HelpDesk/RedirectToHelpDesk" target='_blank' icon={faHeadset} />
      </>}

      <UserMenuItem label='Odhlášení' onClick={logout} icon={faRightFromBracket} />

      {showSettings && <>
        <Divider />

        <UserAvatarMenuItem to='/organization'>
          <Avatar name={user?.organizationName} colorScheme="neutral" size='sm' />
          <Stack gap={0}>
            <Text fontSize="sm" fontWeight='semibold' color='primary.900'>
              {user?.organizationName}
            </Text>
          </Stack>
        </UserAvatarMenuItem>

        {user?.isSupervisor && <>
          {hasExtendedRights && <>
            <UserMenuItem label='Přehled organizací' to='/organization/list' icon={faUsers} />
            <UserMenuItem label='Přehled uživatelů' to='/user/list' icon={faUser} />
            <UserMenuItem label='Přehled flexibilních skupin' to='/flex-group/list' icon={faShareNodes} />

            {user.allowNews && (
              <UserMenuItem label='Správa aktualit' to='/news/admin' icon={faTypewriter} />
            )}

            {user.autorizacePpz && (
              <UserMenuItem label="Správa autorizací PPZ" to="/ppzAuthorization/List" icon={faShieldKeyhole} />
            )}

            <UserMenuItem label='Přehled slev' to='/supervizor/discounts' icon={faBadgePercent} />

            {partner === 'Jap' && <UserMenuItem label='Nastavení data odeslání poukázek' to='/supervizor/setCommissionPaidDates' icon={faMoneyBill} />}
          </>}

          {user.rtpcRights && (
            <Button variant="ghost" as={Link} to="/rtpc">
              Správa RTPC
            </Button>
          )}
        </>}

        {user?.adminCebiaRights && <>
          <Divider />
          {(user?.availablePartners?.length ?? 0) > 1 &&
            <Menu variant='ghost' placement='right'>
              <MenuButton as={Button} variant='ghost' justifyContent='start' px={5} color='primary.900' rightIcon={<ChevronRightIcon />}>
                Přepnout partnera
              </MenuButton>
              <MenuList>
                {user?.availablePartners?.map((partner) => (
                  <MenuItem
                    key={partner.id}
                    onClick={() => switchPartner(partner.id, partner.enumKey)}
                    isDisabled={partner.enumKey === user?.clientKey}
                  >
                    {partner.name ?? partner.enumKey} (Id: {partner.id})
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          }

          <UserMenuItem label='Smazání cache' to='/ClearCache' target='_blank' />
        </>}
      </>}
      <Box h={1} />
    </DropdownMenuItem>
  )
}

const UserAvatarMenuItem = ({ to, children }: { to?: string, children?: React.ReactNode }) => {
  const { onClose } = useContext(DropdownMenuItemContext) || {}

  return <LinkBox as={Grid} p={3} templateColumns="auto 1fr auto" gap={3} alignItems="center" _hover={{ bg: 'gray.50' }} onClick={onClose}>
    {children}
    <LinkOverlay as={Link} to={to}>
      <Icon as={FontAwesomeIcon} icon={faChevronRight} size='sm' color='primary.900' />
    </LinkOverlay>
  </LinkBox>
}

export const UserMenuItem = ({ label, to, onClick, icon, target, isLoading }: { label: string, to?: string, onClick?: () => void, icon?: IconDefinition, target?: string, isLoading?: boolean }) => {
  const { onClose } = useContext(DropdownMenuItemContext) || {}

  const content = <Flex gap={3}>
    {icon && <Icon as={FontAwesomeIcon} icon={icon} mr={2} size='lg' color='primary.900' />}
    <Text color='primary.900'>{label}</Text>
  </Flex>

  return to ?
    <Button variant="ghost" as={Link} to={to} justifyContent='start' px={5} onClick={onClose} target={target} w='full'>
      {content}
    </Button>
    :
    <Button variant="ghost" onClick={() => {
      onClick?.()
      onClose?.()
    }} justifyContent='start' px={5} isLoading={isLoading} w='full'>
      {content}
    </Button>
}
